/**
 * Presence Journey - Main Landing Page
 * 
 * 21-Day Immersion into Presence
 * Vertical unlock path: Day 1 unlocked, Days 2-21 locked until previous day completed
 * 
 * Structure:
 * - Hero section with journey overview
 * - Day cards in vertical scroll
 * - Each day shows: Morning, Action, Midday, Evening, Night
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Leaf, 
  Sun, 
  Sparkles, 
  Moon, 
  Music,
  Lock,
  CheckCircle2,
  ChevronRight,
  ArrowLeft,
  Play
} from 'lucide-react';
import EntryTransition from '../../components/EntryTransition';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// 21-Day Presence Arc — every day trains focus; every action lives outside the phone, in the body, in nature.
// Mornings + evenings use the immersion dot screen as the primary anchor. Days 2–21 are locked until the previous day completes.
const DAY_BLUEPRINT = [
  {
    n: 1,
    theme: 'The Return',
    subtitle: 'This is not something new — this is a return',
    morningDesc: 'A gentle landing into the present moment',
    middayDur: '75 sec',
    middayDesc: 'Replace scrolling with presence',
    eveningDesc: 'Reflection and body awareness before rest',
    nightDur: '15 min',
    nightDesc: 'Affirmations dissolving into sleep',
    action: 'Touch the world consciously. Step outside, touch something real — a tree, the ground, water, even a wall. Feel it… not think about it. No phone. No distraction. Just direct contact with reality.',
  },
  {
    n: 2,
    theme: 'Awareness of Distraction',
    subtitle: 'See the pull — without judgement',
    morningDesc: 'Notice where the mind tries to go before you arrive',
    middayDur: '90 sec',
    middayDesc: 'Catch the distraction. Return to the breath.',
    eveningDesc: 'Where did your attention go today? Without judgement.',
    nightDur: '15 min',
    nightDesc: 'Letting the day settle. Releasing the noise.',
    action: 'For 60 seconds, sit still without your phone. Notice every urge to reach for it — to check, to scroll, to fill the silence. Don\u2019t fight the urge. Just see it. Each time you notice, you\u2019re practising presence.',
  },
  {
    n: 3,
    theme: 'The Breath',
    subtitle: 'The simplest anchor you will ever need',
    morningDesc: 'Slow breathing into the dot — 4 in, 4 hold, 4 out',
    middayDur: '90 sec',
    middayDesc: 'Three slow breaths. That\u2019s it. That\u2019s the whole thing.',
    eveningDesc: 'Notice what changed in the body when you breathed today',
    nightDur: '15 min',
    nightDesc: 'Breath-paced affirmations into sleep',
    action: 'Step outside. Take three slow breaths into the belly — count 4 in, 4 hold, 4 out. Feel the air on your skin as you breathe. The breath was always here, waiting for you to notice.',
  },
  {
    n: 4,
    theme: 'Single Point',
    subtitle: 'Train the eye, train the mind',
    morningDesc: 'The dot. Hold your gaze. Let everything else fall away.',
    middayDur: '90 sec',
    middayDesc: 'Pick one point. Hold it for 90 seconds.',
    eveningDesc: 'Reflect on what arose when the mind had nowhere to wander',
    nightDur: '18 min',
    nightDesc: 'Settling the mind on a single still point',
    action: 'Find one object near you — a leaf, a candle flame, a stone, a flower. Hold your gaze on it for two minutes. When the mind drifts, gently return. This is how focus is built.',
  },
  {
    n: 5,
    theme: "Nature's Rhythm",
    subtitle: 'The world has been waiting for you',
    morningDesc: 'Outdoor immersion — open eyes, soft gaze, no agenda',
    middayDur: '90 sec',
    middayDesc: 'Look up from the screen. Find the sky.',
    eveningDesc: 'What did nature show you today?',
    nightDur: '18 min',
    nightDesc: 'Drift to sleep on the rhythm of the natural world',
    action: 'Step outside and choose one thing in nature — a tree, a bird, the wind in the leaves, a flowing stream. Watch it for two minutes. Don\u2019t describe it to yourself. Just be with it.',
  },
  {
    n: 6,
    theme: 'The Body',
    subtitle: 'Coming home to what you have always been carrying',
    morningDesc: 'Slow body scan — feet to crown',
    middayDur: '90 sec',
    middayDesc: 'Shoulders. Jaw. Belly. Soften each one.',
    eveningDesc: 'The body remembers what the mind forgets',
    nightDur: '18 min',
    nightDesc: 'A slow scan dissolving the day from the body',
    action: 'Sit somewhere quiet for 90 seconds. Move your awareness slowly from your feet up to the crown of your head. No phone. Just feeling. The body is always in the present.',
  },
  {
    n: 7,
    theme: 'Listening',
    subtitle: 'Hear the world without naming it',
    morningDesc: 'Listening immersion — sounds rising and falling',
    middayDur: '90 sec',
    middayDesc: 'Close your eyes for 60 seconds. Just listen.',
    eveningDesc: 'What sounds did you hear today that you usually miss?',
    nightDur: '18 min',
    nightDesc: 'Soundscape into sleep',
    action: 'Step outside, close your eyes for two minutes, and just listen. Don\u2019t name what you hear. Don\u2019t try to identify it. Let sound move through you like wind moves through a tree.',
  },
  {
    n: 8,
    theme: 'Soft Eyes',
    subtitle: 'Take in everything without focusing on anything',
    morningDesc: 'Open peripheral awareness — the dot expands outward',
    middayDur: '90 sec',
    middayDesc: 'Soften the eyes. Widen the field. Drop into now.',
    eveningDesc: 'How does the world feel when you stop hunting it with your gaze?',
    nightDur: '18 min',
    nightDesc: 'Vision dissolves into sleep',
    action: 'Stand or sit somewhere with a wide view. Soften your gaze and let your peripheral vision open out as wide as it will go. You\u2019re not looking AT anything — you\u2019re receiving everything. Stay there for two minutes.',
  },
  {
    n: 9,
    theme: 'One Thing',
    subtitle: 'The lost art of doing one thing at a time',
    morningDesc: 'Eating, drinking, breathing — all are practices today',
    middayDur: '90 sec',
    middayDesc: 'Whatever you\u2019re doing — do only that for 90 seconds',
    eveningDesc: 'What changed when you stopped splitting yourself?',
    nightDur: '18 min',
    nightDesc: 'Releasing the urge to do more than one thing',
    action: 'Eat one meal today with no phone, no screen, no podcast. Taste the food. Feel the chewing. Notice when you\u2019re full. One meal. One thing. Full presence.',
  },
  {
    n: 10,
    theme: 'The Pause',
    subtitle: 'The space between the urge and the action',
    morningDesc: 'Sit with the urge — let it rise, let it pass',
    middayDur: '90 sec',
    middayDesc: 'Before you reach for the phone — pause. Count to five.',
    eveningDesc: 'How many pauses did you take today?',
    nightDur: '18 min',
    nightDesc: 'Letting the nervous system find its own rhythm',
    action: 'Every time you go to pick up your phone today, pause. Count slowly to five. Then ask: \u201cdo I actually need this right now?\u201d The pause is the practice. The answer doesn\u2019t matter.',
  },
  {
    n: 11,
    theme: 'Ground Beneath',
    subtitle: 'The earth has been holding you all along',
    morningDesc: 'Feet on the floor — feel the support',
    middayDur: '90 sec',
    middayDesc: 'Press your feet into the ground. Feel held.',
    eveningDesc: 'What did the earth feel like under you today?',
    nightDur: '18 min',
    nightDesc: 'Sinking into the support of the ground',
    action: 'Take your shoes off. Step onto grass, sand, soil, or stone — anything natural. Stand there for five minutes. Feel the texture, the temperature, the support. The ground has always been there.',
  },
  {
    n: 12,
    theme: 'Sky',
    subtitle: 'Look up — the world is bigger than your inbox',
    morningDesc: 'Wide-open immersion — sky-scale awareness',
    middayDur: '90 sec',
    middayDesc: 'Step away from the screen. Look up.',
    eveningDesc: 'How did it feel to remember the sky?',
    nightDur: '18 min',
    nightDesc: 'Mind opening as wide as the sky',
    action: 'Go outside. Look up. Watch the sky — clouds moving, light changing, birds passing — for three full minutes. No phone. No photo. Just you and the sky. Notice how spacious you feel afterwards.',
  },
  {
    n: 13,
    theme: 'The Walk',
    subtitle: 'Walking as meditation',
    morningDesc: 'Walking immersion — breath, step, breath, step',
    middayDur: '90 sec',
    middayDesc: 'Even ten steps with full presence is a practice',
    eveningDesc: 'How did your body feel after walking with awareness?',
    nightDur: '20 min',
    nightDesc: 'Releasing the day with each imagined step',
    action: 'Take a ten-minute walk today with no phone, no music, no podcast. Just the rhythm of your breath and your feet on the ground. Notice five things you\u2019ve never noticed before on that route.',
  },
  {
    n: 14,
    theme: 'Water',
    subtitle: 'Slowing down a moment you do every day',
    morningDesc: 'Sensation immersion — temperature, texture, flow',
    middayDur: '90 sec',
    middayDesc: 'Wash your hands slowly. Feel every sensation.',
    eveningDesc: 'When did water feel most alive to you today?',
    nightDur: '20 min',
    nightDesc: 'Letting the body wash clean of the day',
    action: 'Wash your hands today like it\u2019s the first time. Feel the temperature. Feel the soap. Feel the water leaving the skin. A 30-second act becomes a doorway when you fully arrive in it.',
  },
  {
    n: 15,
    theme: 'Conversation',
    subtitle: 'Presence is the highest form of love',
    morningDesc: 'Listening immersion — making space for another',
    middayDur: '90 sec',
    middayDesc: 'In your next conversation — phone away, eyes up',
    eveningDesc: 'Who did you really see today?',
    nightDur: '20 min',
    nightDesc: 'Releasing every conversation back to the day',
    action: 'Have one full conversation today with your phone in another room. Look the person in the eyes. Listen without preparing your reply. This is the most powerful gift you can give another human.',
  },
  {
    n: 16,
    theme: 'Boredom',
    subtitle: 'On the other side of boredom is presence',
    morningDesc: 'Sitting with nothing — letting the mind unwind',
    middayDur: '90 sec',
    middayDesc: 'Don\u2019t fill the gap. Stay in it.',
    eveningDesc: 'What arose when you let yourself be bored?',
    nightDur: '20 min',
    nightDesc: 'Permission to do absolutely nothing',
    action: 'Sit with nothing for five minutes. No phone, no music, no book, no task. Let the urge to fill the silence rise — then let it pass. On the other side of boredom is the present moment.',
  },
  {
    n: 17,
    theme: 'The Witness',
    subtitle: 'You are not your thoughts',
    morningDesc: 'Watching thought arise and dissolve',
    middayDur: '90 sec',
    middayDesc: 'Watch one thought come — and let it go',
    eveningDesc: 'What did the witness within you see today?',
    nightDur: '20 min',
    nightDesc: 'Resting as awareness itself',
    action: 'For two minutes today, sit and watch your thoughts arrive — like clouds across a sky. Don\u2019t engage them. Don\u2019t analyse them. Just notice them coming and going. You are the sky, not the weather.',
  },
  {
    n: 18,
    theme: 'Heart Awareness',
    subtitle: 'The heart has been beating for you this whole time',
    morningDesc: 'Hand on chest — feeling the rhythm beneath',
    middayDur: '90 sec',
    middayDesc: 'Hand on heart. Three breaths. Return.',
    eveningDesc: 'What does it mean to feel held by your own heart?',
    nightDur: '20 min',
    nightDesc: 'Heart-rhythm slowing into sleep',
    action: 'Place your hand on your chest. Feel the heartbeat. Stay there for two minutes. This rhythm has been with you every moment of your life — and it\u2019s asking for nothing in return.',
  },
  {
    n: 19,
    theme: 'Felt Gratitude',
    subtitle: 'Don\u2019t list it — feel it',
    morningDesc: 'A gratitude immersion that lives in the body',
    middayDur: '90 sec',
    middayDesc: 'Three things you can FEEL grateful for right now',
    eveningDesc: 'Where in the body did gratitude land today?',
    nightDur: '20 min',
    nightDesc: 'Falling asleep inside a felt gratitude',
    action: 'Choose three things in your day to feel grateful for — not list, FEEL. Stay with each one until you actually feel it in your chest, your belly, your face. Felt gratitude is medicine. Listed gratitude is just words.',
  },
  {
    n: 20,
    theme: 'Whole Self',
    subtitle: 'Bringing every practice into one',
    morningDesc: 'Integration immersion — breath, body, gaze, heart',
    middayDur: '90 sec',
    middayDesc: 'A 90-second integration of everything you\u2019ve learned',
    eveningDesc: 'What practice has become yours?',
    nightDur: '20 min',
    nightDesc: 'Drawing twenty days of practice into the body',
    action: 'Spend ten minutes outside today. Walk, sit, or stand — your choice. Practise everything: breath, soft eyes, body awareness, listening, heart. All of it, all at once. This is what presence feels like.',
  },
  {
    n: 21,
    theme: 'Presence as Practice',
    subtitle: 'This is the beginning, not the end',
    morningDesc: 'A final immersion to anchor what you\u2019ve become',
    middayDur: '90 sec',
    middayDesc: 'Choose your daily anchor. The one you\u2019ll keep.',
    eveningDesc: 'What does it mean to live this way every day?',
    nightDur: '21 min',
    nightDesc: 'Sealing the 21 days into who you are now',
    action: 'Choose ONE practice from these 21 days that will live with you forever. The breath. The pause. The walk. Soft eyes. Hand on heart. Whichever one called to you most. From today, this is your anchor — yours alone, for life.',
  },
];

const DAY_CONTENT = DAY_BLUEPRINT.reduce((acc, d) => {
  acc[d.n] = {
    theme: d.theme,
    subtitle: d.subtitle,
    morning: { title: 'Morning Immersion', duration: '4 min', description: d.morningDesc, unlocked: true },
    action: { title: 'Daily Action', prompt: d.action, unlocked: true },
    midday: { title: 'Midday Interrupt', duration: d.middayDur, description: d.middayDesc, unlocked: true },
    evening: { title: 'Evening Immersion', duration: '6 min', description: d.eveningDesc, unlocked: true },
    night: { title: 'Night Audio', duration: d.nightDur, description: d.nightDesc, unlocked: true },
  };
  return acc;
}, {});

// Session Card Component
const SessionCard = ({ session, type, dayNumber, onSelect, completed }) => {
  const icons = {
    morning: Sun,
    action: Sparkles,
    midday: Leaf,
    evening: Moon,
    night: Music,
  };
  const Icon = icons[type] || Leaf;
  const isLocked = !session.unlocked;

  return (
    <motion.button
      onClick={() => !isLocked && onSelect(type, dayNumber)}
      disabled={isLocked}
      whileHover={!isLocked ? { scale: 1.02 } : {}}
      whileTap={!isLocked ? { scale: 0.98 } : {}}
      className={`w-full p-4 rounded-xl flex items-center gap-4 text-left transition-all duration-300 ${
        isLocked 
          ? 'bg-[#1A1A18]/50 opacity-50 cursor-not-allowed' 
          : completed
            ? 'bg-emerald-900/20 border border-emerald-700/30'
            : 'bg-[#1A1A18] border border-[#2A2A28] hover:border-emerald-700/40'
      }`}
      data-testid={`session-${type}-day-${dayNumber}`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
        isLocked 
          ? 'bg-[#2A2A28]' 
          : completed 
            ? 'bg-emerald-700/30'
            : 'bg-emerald-900/30'
      }`}>
        {isLocked ? (
          <Lock size={16} className="text-[#6A6A68]" />
        ) : completed ? (
          <CheckCircle2 size={16} className="text-emerald-500" />
        ) : (
          <Icon size={16} className="text-emerald-400" />
        )}
      </div>
      
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium ${
          isLocked ? 'text-[#6A6A68]' : 'text-[#F2EFE9]'
        }`}>
          {session.title}
        </p>
        {session.duration && (
          <p className="text-xs text-[#8A8A88] mt-0.5">{session.duration}</p>
        )}
      </div>

      {!isLocked && !completed && (
        <ChevronRight size={18} className="text-emerald-500/50" />
      )}
    </motion.button>
  );
};

// Day Card Component
const DayCard = ({ day, dayNumber, isUnlocked, isActive, onExpand, expanded, onSessionSelect, completedSessions }) => {
  const isCompleted = completedSessions?.length >= 5;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: dayNumber * 0.05, duration: 0.5 }}
      className={`rounded-2xl overflow-hidden transition-all duration-500 ${
        !isUnlocked 
          ? 'opacity-40' 
          : isActive 
            ? 'ring-2 ring-emerald-600/50'
            : ''
      }`}
    >
      {/* Day Header */}
      <motion.button
        onClick={() => isUnlocked && onExpand(dayNumber)}
        disabled={!isUnlocked}
        className={`w-full p-5 flex items-center gap-4 text-left ${
          !isUnlocked 
            ? 'bg-[#0F0F0E] cursor-not-allowed' 
            : 'bg-[#141412] hover:bg-[#1A1A18]'
        } transition-colors duration-300`}
        data-testid={`day-${dayNumber}-header`}
      >
        {/* Day Number */}
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-lg font-medium ${
          !isUnlocked 
            ? 'bg-[#1A1A18] text-[#4A4A48]'
            : isCompleted
              ? 'bg-emerald-700/30 text-emerald-400'
              : isActive
                ? 'bg-emerald-600/20 text-emerald-400'
                : 'bg-[#1A1A18] text-[#9A9A98]'
        }`}>
          {!isUnlocked ? (
            <Lock size={18} />
          ) : isCompleted ? (
            <CheckCircle2 size={20} />
          ) : (
            dayNumber
          )}
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className={`font-medium ${
              !isUnlocked ? 'text-[#5A5A58]' : 'text-[#F2EFE9]'
            }`}>
              Day {dayNumber}
            </h3>
            {isActive && (
              <span className="px-2 py-0.5 text-[10px] uppercase tracking-wider bg-emerald-600/20 text-emerald-400 rounded-full">
                Today
              </span>
            )}
          </div>
          <p className={`text-sm mt-0.5 ${
            !isUnlocked ? 'text-[#4A4A48]' : 'text-[#8A8A88]'
          }`}>
            {day.theme} — {day.subtitle}
          </p>
        </div>

        {isUnlocked && (
          <motion.div
            animate={{ rotate: expanded ? 90 : 0 }}
            transition={{ duration: 0.2 }}
          >
            <ChevronRight size={20} className="text-[#6A6A68]" />
          </motion.div>
        )}
      </motion.button>

      {/* Expanded Content */}
      <AnimatePresence>
        {expanded && isUnlocked && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-5 pt-0 space-y-3 bg-[#141412]">
              {/* Session Cards */}
              <SessionCard 
                session={day.morning} 
                type="morning" 
                dayNumber={dayNumber}
                onSelect={onSessionSelect}
                completed={completedSessions?.includes('morning')}
              />
              <SessionCard 
                session={day.action} 
                type="action" 
                dayNumber={dayNumber}
                onSelect={onSessionSelect}
                completed={completedSessions?.includes('action')}
              />
              <SessionCard 
                session={day.midday} 
                type="midday" 
                dayNumber={dayNumber}
                onSelect={onSessionSelect}
                completed={completedSessions?.includes('midday')}
              />
              <SessionCard 
                session={day.evening} 
                type="evening" 
                dayNumber={dayNumber}
                onSelect={onSessionSelect}
                completed={completedSessions?.includes('evening')}
              />
              <SessionCard 
                session={day.night} 
                type="night" 
                dayNumber={dayNumber}
                onSelect={onSessionSelect}
                completed={completedSessions?.includes('night')}
              />

              {/* Daily Action Card (special) — every day has a sensory action prompt */}
              {day.action?.prompt && (
                <div className="mt-4 p-4 rounded-xl bg-emerald-900/10 border border-emerald-800/20">
                  <p className="text-xs uppercase tracking-[0.2em] text-emerald-500/70 mb-2">
                    Today's Intention
                  </p>
                  <p className="text-[#F2EFE9] text-sm leading-relaxed font-light italic">
                    "{day.action.prompt}"
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Main Component
export const PresenceJourney = () => {
  const navigate = useNavigate();
  const [showEntryTransition, setShowEntryTransition] = useState(true);
  const [expandedDay, setExpandedDay] = useState(1);
  const [currentDay, setCurrentDay] = useState(1); // User's progress
  const [completedSessions, setCompletedSessions] = useState({}); // { 1: ['morning', 'midday'], 2: [] }
  const [loading, setLoading] = useState(true);

  // Fetch user progress
  useEffect(() => {
    fetchProgress();
  }, []);

  const fetchProgress = async () => {
    const token = localStorage.getItem('reclaim_token');
    if (!token) {
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/presence-journey/progress`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCurrentDay(data.current_day || 1);
        setCompletedSessions(data.completed_sessions || {});
      }
    } catch (error) {
      console.error('Failed to fetch presence progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSessionSelect = (sessionType, dayNumber) => {
    // Navigate to appropriate session
    const routes = {
      morning: `/reclaim-presence/day/${dayNumber}/morning`,
      midday: `/reclaim-presence/midday`,
      evening: `/reclaim-presence/day/${dayNumber}/evening`,
      night: `/reclaim-presence/day/${dayNumber}/night`,
      action: null, // Action is just a prompt, no separate page
    };

    if (routes[sessionType]) {
      navigate(routes[sessionType]);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handleEntryComplete = () => {
    setShowEntryTransition(false);
  };

  // Quick start - go directly to morning immersion
  const handleQuickStart = () => {
    navigate(`/reclaim-presence/day/${currentDay}/morning`);
  };

  return (
    <div className="min-h-screen bg-[#0A0A09]">
      {/* Entry Transition */}
      <EntryTransition 
        isActive={showEntryTransition} 
        onComplete={handleEntryComplete}
        duration={2500}
      />

      {/* Subtle texture */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-[0.02]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-30 bg-[#0A0A09]/90 backdrop-blur-lg border-b border-[#1A1A18]">
        <div className="px-5 py-4 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="p-2 -ml-2 rounded-lg hover:bg-[#1A1A18] transition-colors"
            data-testid="presence-back-btn"
          >
            <ArrowLeft size={20} className="text-[#9A9A98]" />
          </button>
          
          <div className="text-center">
            <p className="text-[10px] uppercase tracking-[0.25em] text-emerald-500/70">
              Reclaim Our Presence
            </p>
            <p className="text-[#F2EFE9] text-sm font-medium mt-0.5">
              21-Day Immersion
            </p>
          </div>

          <div className="w-8" /> {/* Spacer for alignment */}
        </div>
      </header>

      {/* Hero Section */}
      <section className="px-6 py-10 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: showEntryTransition ? 0 : 1, y: showEntryTransition ? 20 : 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          {/* Icon */}
          <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-emerald-700/30 to-teal-800/30 flex items-center justify-center border border-emerald-600/20">
            <Leaf size={36} className="text-emerald-400" />
          </div>

          <h1 className="font-['Playfair_Display'] text-3xl text-[#F2EFE9] mb-3">
            Return to Presence
          </h1>
          
          <p className="text-[#8A8A88] text-base leading-relaxed max-w-sm mx-auto mb-8">
            21 days to replace unconscious scrolling with guided state shifts into presence
          </p>

          {/* Quick Start Button */}
          <motion.button
            onClick={handleQuickStart}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="inline-flex items-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-medium shadow-lg shadow-emerald-600/30 hover:shadow-emerald-600/40 transition-shadow"
            data-testid="presence-quick-start"
          >
            <Play size={18} fill="white" />
            Begin Day {currentDay}
          </motion.button>

          {/* Progress Indicator */}
          <div className="mt-8 flex items-center justify-center gap-2">
            <div className="flex gap-1">
              {[...Array(21)].map((_, i) => (
                <div
                  key={i}
                  className={`w-2 h-2 rounded-full transition-all ${
                    i < currentDay - 1
                      ? 'bg-emerald-500'
                      : i === currentDay - 1
                        ? 'bg-emerald-400 ring-2 ring-emerald-400/30'
                        : 'bg-[#2A2A28]'
                  }`}
                />
              ))}
            </div>
          </div>
          <p className="text-[#6A6A68] text-xs mt-3">
            Day {currentDay} of 21
          </p>
        </motion.div>
      </section>

      {/* Day Cards */}
      <main className="px-4 pb-32 space-y-3">
        {[...Array(21)].map((_, i) => {
          const dayNumber = i + 1;
          const day = DAY_CONTENT[dayNumber];
          const isUnlocked = dayNumber <= currentDay;
          const isActive = dayNumber === currentDay;

          return (
            <DayCard
              key={dayNumber}
              day={day}
              dayNumber={dayNumber}
              isUnlocked={isUnlocked}
              isActive={isActive}
              expanded={expandedDay === dayNumber}
              onExpand={setExpandedDay}
              onSessionSelect={handleSessionSelect}
              completedSessions={completedSessions[dayNumber] || []}
            />
          );
        })}
      </main>

      {/* Bottom Fade */}
      <div className="fixed bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-[#0A0A09] to-transparent pointer-events-none z-20" />
    </div>
  );
};

export default PresenceJourney;
